import { FileText, MessageSquare, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWorkflowMetrics } from '../hooks/useWorkflowMetrics';
import { 
  REQUEST_STATUS, 
  QUOTE_STATUS, 
  JOB_STATUS, 
  getRequestStatusLabel,
  getQuoteStatusLabel,
  getJobStatusLabel,
} from '@cleanpro/common-ui';

interface WorkflowStage {
  id: string;
  title: string;
  count: number;
  status: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  subItems: Array<{ label: string; count: number }>;
  route: string;
}

export default function WorkflowSection() {
  const { workflowMetrics } = useWorkflowMetrics();
  const navigate = useNavigate();

  const workflowStages: WorkflowStage[] = [
    {
      id: 'requests',
      title: 'New',
      count: workflowMetrics.requests.new,
      status: 'Requests',
      icon: FileText,
      color: 'orange',
      subItems: [
        { label: getRequestStatusLabel(REQUEST_STATUS.ASSESSED), count: workflowMetrics.requests.assessed },
        { label: getRequestStatusLabel(REQUEST_STATUS.QUOTED), count: workflowMetrics.requests.quoted },
      ],
      route: '/requests'
    },
    {
      id: 'quotes',
      title: 'Require Action',
      count: workflowMetrics.quotes.draft + workflowMetrics.quotes.awaitingResponse + workflowMetrics.quotes.approved + workflowMetrics.quotes.pendingAssignment + workflowMetrics.quotes.changesRequested,
      status: 'Quotes',
      icon: MessageSquare,
      color: 'purple',
      subItems: [
        { label: getQuoteStatusLabel(QUOTE_STATUS.DRAFT), count: workflowMetrics.quotes.draft },
        { label: getQuoteStatusLabel(QUOTE_STATUS.AWAITING_RESPONSE), count: workflowMetrics.quotes.awaitingResponse },
        { label: getQuoteStatusLabel(QUOTE_STATUS.APPROVED), count: workflowMetrics.quotes.approved },
        { label: getQuoteStatusLabel(QUOTE_STATUS.CHANGES_REQUESTED), count: workflowMetrics.quotes.changesRequested },
        { label: getQuoteStatusLabel(QUOTE_STATUS.PENDING_ASSIGNMENT), count: workflowMetrics.quotes.pendingAssignment }
      ],
      route: '/quotes'
    },
    {
      id: 'jobs',
      title: 'Upcoming Jobs',
      count: workflowMetrics.jobs.upcomingJobs,
      status: 'Jobs',
      icon: Calendar,
      color: 'green',
      subItems: [
        { label: getJobStatusLabel(JOB_STATUS.REQUESTED), count: workflowMetrics.jobs.requested },
        { label: getJobStatusLabel(JOB_STATUS.CONFIRMED), count: workflowMetrics.jobs.confirmed },
        { label: getJobStatusLabel(JOB_STATUS.IN_PROGRESS), count: workflowMetrics.jobs.inProgress },
        { label: getJobStatusLabel(JOB_STATUS.EN_ROUTE), count: workflowMetrics.jobs.enRoute }
      ],
      route: '/jobs'
    },
    // {
    //   id: 'invoices',
    //   title: 'Awaiting payment',
    //   count: workflowMetrics.invoices.outstandingValue,
    //   status: 'Invoices',
    //   icon: Receipt,
    //   color: 'blue',
    //   subItems: [
    //     { label: getInvoiceStatusLabel(INVOICE_STATUS.SENT), count: workflowMetrics.invoices.sent },
    //     { label: getInvoiceStatusLabel(INVOICE_STATUS.OVERDUE), count: workflowMetrics.invoices.overdue }
    //   ],
    //   route: '/invoices'
    // }
  ];

  const getColorClasses = (color: string) => {
    const colorMap = {
      orange: 'bg-orange-50 border-orange-200 text-orange-700',
      purple: 'bg-purple-50 border-purple-200 text-purple-700',
      green: 'bg-green-50 border-green-200 text-green-700',
      blue: 'bg-blue-50 border-blue-200 text-blue-700'
    };
    return colorMap[color as keyof typeof colorMap] || 'bg-gray-50 border-gray-200 text-gray-700';
  };

  const getIconColorClasses = (color: string) => {
    const colorMap = {
      orange: 'text-orange-600',
      purple: 'text-purple-600',
      green: 'text-green-600',
      blue: 'text-blue-600'
    };
    return colorMap[color as keyof typeof colorMap] || 'text-gray-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Workflow</h2>
      
      {/* Progress Bar */}
      <div className="flex h-2 mb-6 rounded-full overflow-hidden bg-gray-100">
        <div className="bg-orange-500 flex-1"></div>
        <div className="bg-purple-500 flex-1"></div>
        <div className="bg-green-500 flex-1"></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {workflowStages.map((stage) => {
          const IconComponent = stage.icon;
          return (
            <div 
              key={stage.id}
              onClick={() => navigate(stage.route)}
              className={`border rounded-lg p-4 cursor-pointer hover:shadow-md transition-all ${getColorClasses(stage.color)}`}
            >
              <div className="flex items-start justify-between mb-3">
                <IconComponent className={`h-5 w-5 ${getIconColorClasses(stage.color)}`} />
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  {stage.status}
                </span>
              </div>
              
              <div className="mb-3">
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {stage.count}
                </div>
                <div className="text-sm font-medium text-gray-700">
                  {stage.title}
                </div>
              </div>

              <div className="space-y-1">
                {stage.subItems.map((item, index) => (
                  <div key={index} className="flex justify-between text-xs text-gray-600">
                    <span>{item.label}</span>
                    <span>({item.count})</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
